// ============================================================================
// FSA ADJUSTMENTS TAB  (FSA-2.2 Adjustments)
// ============================================================================
// Per-engagement adjustment-journal list. Drives create/edit/view of a single
// JE through FsaAdjustmentJournalModal. Live tally + status chips per row.
// Posting / Reversal happen inside the modal — the row action menu only
// opens the modal in the appropriate mode.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  SyutGrid,
  type SyutGridColumn,
  MinimizedModalStrip,
  ActionMenu,
  type ActionMenuItem,
} from 'components/ui';
import { useFsaAdjustmentJournals } from 'hooks/api/fsa/useFsaAdjustmentJournals';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { fsaAdjustmentJournalService } from 'api/services/fsa/fsaAdjustmentJournalService';
import FsaAdjustmentJournalModal from 'components/modals/modules/fsa/FsaAdjustmentJournalModal';
import FsaAdjustmentJournalPdfPreviewModal, { type JePdfMode } from 'components/modals/modules/fsa/FsaAdjustmentJournalPdfPreviewModal';
import { ConfirmDialog } from 'components/modals';
import type { ID } from 'api/types';
import type {
  AdjustmentJournal,
  AdjustmentJournalQueryParameters,
  AdjustmentJournalStatus,
} from 'api/types/fsa/adjustmentJournal.types';

interface Props {
  engagementId: ID;
  tabId?: string;
  moduleName?: string;
  /**
   * Fires whenever a journal is created, saved, posted, reversed, or
   * deleted on this engagement. Parent screen forwards this to the
   * Trial Balance tab as a refresh signal so its Journal Net + Audited
   * columns stay in step without the auditor having to switch tabs.
   * Optional — older call sites work unchanged.
   */
  onJournalChanged?: () => void;
}

type Mode = 'create' | 'edit' | 'view';

const FsaAdjustmentsTab: React.FC<Props> = ({ engagementId, tabId, moduleName, onJournalChanged }) => {
  const [params, setParams] = useState<AdjustmentJournalQueryParameters>({ page: 1, pageSize: 25 });
  const { data, pagination, loading, refetch } = useFsaAdjustmentJournals(engagementId, params);

  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const journalModalMinimizeRef = useRef<(() => void) | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<Mode>('create');
  const [activeJournalId, setActiveJournalId] = useState<ID | null>(null);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');

  const [deleteTarget, setDeleteTarget] = useState<AdjustmentJournal | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // FSA-2.4 Phase 2 — Status filter pills (DRAFT/POSTED/REVERSED)
  // replaced with a single "Show deleted" toggle. Off by default so
  // the auditor sees only active JEs; toggle on to surface the
  // soft-deleted history (audit reference).
  const [showDeleted, setShowDeleted] = useState<boolean>(false);

  // PDF bundle preview — toggles between Working Papers and Client Schedule
  // inside the modal. The toolbar button opens with the user's last choice
  // (defaults to Working Papers on first open).
  const [pdfPreview, setPdfPreview] = useState<{ open: boolean; mode: JePdfMode }>({
    open: false,
    mode: 'working-papers',
  });

  const openModalFor = useCallback((mode: Mode, journal?: AdjustmentJournal) => {
    if (mode !== 'create' && journal && tabId) {
      const modalId = `${tabId}-fsa-adj-journal-${mode}-${journal.id}`;
      if (isMinimized(modalId)) {
        const m = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        m?.onRestore?.();
        return;
      }
    }
    if (modalOpen && journalModalMinimizeRef.current) {
      journalModalMinimizeRef.current();
    }
    if (mode === 'create') {
      const newId = String(++createInstanceCounter.current);
      setCreateInstanceId(newId);
      setActiveJournalId(null);
    } else {
      setActiveJournalId(journal?.id ?? null);
    }
    setModalMode(mode);
    setModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized, modalOpen]);

  const handleRestoreModal = useCallback((restoredMode: Mode, journalId?: ID | null, instanceId?: string) => {
    if (restoredMode === 'create' && instanceId) setCreateInstanceId(instanceId);
    if (restoredMode !== 'create') setActiveJournalId(journalId ?? null);
    setModalMode(restoredMode);
    setModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((j: AdjustmentJournal) => {
    // FSA-2.4 Phase 2 — strict-save flow. Any active JE can be
    // deleted; the backend enforces the note-status lock (rejects
    // with a clear message if a touched Note is APPROVED).
    setDeleteTarget(j);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await fsaAdjustmentJournalService.remove(deleteTarget.id, deleteTarget.rowVersion);
      notificationService.toast.success(`Journal ${deleteTarget.journalNumber} deleted.`);
      setDeleteTarget(null);
      setIsDeleting(false);
      refetch();
      onJournalChanged?.();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Error deleting journal.');
    }
  }, [deleteTarget, refetch]);

  const handlePageChange = useCallback((idx: number) => {
    setParams(p => ({ ...p, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setParams(p => ({ ...p, pageSize: size, page: 1 }));
  }, []);

  const columns: SyutGridColumn<AdjustmentJournal>[] = useMemo(() => [
    {
      id: 'journalNumber',
      header: 'Journal #',
      accessorKey: 'journalNumber',
      size: 120,
      enableSorting: true,
      cell: ({ row }: any) => (
        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {row.original.journalNumber}
        </span>
      ),
    },
    {
      id: 'journalDate',
      header: 'Date',
      accessorKey: 'journalDate',
      size: 110,
      enableSorting: true,
      cell: ({ row }: any) => formatDate(row.original.journalDate),
    },
    {
      id: 'narration',
      header: 'Narration',
      accessorKey: 'narration',
      cell: ({ row }: any) => (
        <span style={{ color: 'var(--text-primary)' }}>
          {row.original.narration ?? <em style={{ color: 'var(--text-secondary)' }}>—</em>}
        </span>
      ),
    },
    {
      id: 'lineCount',
      header: 'Lines',
      size: 70,
      cell: ({ row }: any) => row.original.entries?.length ?? 0,
    },
    {
      id: 'totalDebit',
      header: 'Total Dr',
      size: 130,
      cell: ({ row }: any) => (
        <span style={amountCellStyle}>{formatAmount(row.original.totalDebit)}</span>
      ),
    },
    {
      id: 'totalCredit',
      header: 'Total Cr',
      size: 130,
      cell: ({ row }: any) => (
        <span style={amountCellStyle}>{formatAmount(row.original.totalCredit)}</span>
      ),
    },
    {
      id: 'tally',
      header: 'Tally',
      size: 110,
      cell: ({ row }: any) => {
        const diff = row.original.tallyDifference;
        const bal = Math.abs(diff) < 0.005;
        return (
          <span style={chip(bal ? '#dcfce7' : '#fef2f2', bal ? '#166534' : '#dc2626')}>
            {bal ? '✓ ' : '⚠ '}
            {formatAmount(diff)}
          </span>
        );
      },
    },
    {
      // FSA-2.4 Phase 2 — status simplified to ACTIVE / DELETED.
      // Legacy DRAFT/POSTED/REVERSED no longer surfaces in the UI.
      id: 'statusCode',
      header: 'Status',
      size: 100,
      cell: ({ row }: any) => {
        const j: AdjustmentJournal = row.original;
        if (j.deletedAt) {
          return <span style={chip('#fee2e2', '#991b1b')}>DELETED</span>;
        }
        return <span style={chip('#dcfce7', '#166534')}>ACTIVE</span>;
      },
    },
    {
      id: 'actions',
      header: '',
      size: 60,
      cell: ({ row }: any) => {
        const j: AdjustmentJournal = row.original;
        const items: ActionMenuItem[] = [
          { id: 'view', label: 'View', icon: <IconSystem.eye size={14} />, onClick: () => openModalFor('view', j) },
        ];
        // FSA-2.4 Phase 2 — active JEs offer Edit + Delete; deleted
        // JEs only show View (audit-history reference). Note-status
        // lock is enforced server-side; surfaces inside the modal.
        if (!j.deletedAt) {
          items.push({ id: 'edit',   label: 'Edit',   icon: <IconSystem.edit  size={14} />, onClick: () => openModalFor('edit', j) });
          items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={14} />, onClick: () => handleDeleteClick(j), variant: 'danger' });
        }
        return <ActionMenu items={items} />;
      },
    },
  ], [openModalFor, handleDeleteClick]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={toolbarStyle}>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
          {loading ? 'Loading…' : `${pagination.totalItems} adjustment journal${pagination.totalItems === 1 ? '' : 's'}`}
        </div>

        {/* FSA-2.4 Phase 2 — "Show deleted" toggle replaces the
            legacy DRAFT/POSTED/REVERSED filter pills. Off by default. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12, fontSize: '0.78rem', color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => {
              const next = e.target.checked;
              setShowDeleted(next);
              setParams(p => ({ ...p, includeDeleted: next, page: 1 }));
            }}
            style={{ cursor: 'pointer' }}
          />
          Show deleted
        </label>

        {tabId && <MinimizedModalStrip tabId={tabId} />}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setPdfPreview({ open: true, mode: 'working-papers' })}
          title="Export bundle PDF — Working Papers / Client Schedule (toggle inside)"
          disabled={pagination.totalItems === 0}
        >
          <IconSystem.print size={14} /> Print Bundle
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={refetch}
          title="Refresh"
          disabled={loading}
        >
          <IconSystem.refresh size={14} />
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => openModalFor('create')}
        >
          <IconSystem.add size={14} /> New Adjustment Journal
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <SyutGrid
          data={data as any}
          columns={columns as any}
          enableSorting
          enablePagination
          manualPagination
          totalCount={pagination.totalItems}
          currentPage={(params.page ?? 1) - 1}
          pageSize={params.pageSize ?? 25}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyMessage={loading ? 'Loading…' : 'No adjustment journals yet.'}
        />
      </div>

      <FsaAdjustmentJournalModal
        isOpen={modalOpen}
        mode={modalMode}
        engagementId={engagementId}
        journalId={activeJournalId}
        onClose={() => setModalOpen(false)}
        onSaved={() => { refetch(); onJournalChanged?.(); }}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        minimizeRef={journalModalMinimizeRef}
        createInstanceId={createInstanceId}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        title="Delete Adjustment Journal"
        message={`Delete journal ${deleteTarget?.journalNumber ?? ''}? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <FsaAdjustmentJournalPdfPreviewModal
        isOpen={pdfPreview.open}
        engagementId={engagementId}
        initialMode={pdfPreview.mode}
        onClose={() => setPdfPreview(p => ({ ...p, open: false }))}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// helpers + styles
// ---------------------------------------------------------------------------

const formatDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};
const formatAmount = (n: number): string =>
  Math.abs(n) < 0.005
    ? '0.00'
    : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusBg = (s: AdjustmentJournalStatus): string =>
  s === 'POSTED' ? '#dcfce7' : s === 'REVERSED' ? '#fee2e2' : '#dbeafe';
const statusFg = (s: AdjustmentJournalStatus): string =>
  s === 'POSTED' ? '#166534' : s === 'REVERSED' ? '#991b1b' : '#1e40af';

const chip = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 999,
  background: bg, color, fontSize: '0.7rem', fontWeight: 600,
});

const toolbarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const amountCellStyle: React.CSSProperties = {
  display: 'block', textAlign: 'right',
  fontVariantNumeric: 'tabular-nums', fontWeight: 600,
};
const filterPillStyle: React.CSSProperties = {
  padding: '2px 10px', borderRadius: 999,
  border: '1px solid var(--border-color)',
  background: 'transparent', color: 'var(--text-secondary)',
  cursor: 'pointer', fontSize: '0.75rem',
};
const filterPillActiveStyle: React.CSSProperties = {
  background: 'var(--primary-color, #2563eb)', color: 'white',
  borderColor: 'var(--primary-color, #2563eb)',
};

export default FsaAdjustmentsTab;
